/* Migratiemachine Fase 1 — CSV-import met mapping, dry-run en commit. */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, PlayCircle, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const ENTITY_FIELDS = {
  parents: ["voornaam", "achternaam", "email", "telefoon", "adres", "postcode", "stad"],
  children: ["voornaam", "achternaam", "geboortedatum", "bsn", "location_id", "group_id"],
  locations: ["name", "lrk_number", "adres", "postcode", "plaats", "telefoon", "email", "capaciteit"],
};

export default function MigratiePage() {
  const [batches, setBatches] = useState([]);
  const [entity, setEntity] = useState("parents");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dryRun, setDryRun] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Bereken huidige wizard-stap (voor stepper-indicator)
  const currentStep = !active ? 1 : (active.status === "uploaded" ? 2 : (dryRun ? 3 : 2));

  const onDropFiles = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!/\.(csv|txt|xlsx)$/i.test(f.name)) { toast.error("Alleen CSV/XLSX toegestaan"); return; }
    setFile(f);
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/migratie");
      setBatches(r.data || []);
    } catch (e) { /* stil */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Kies eerst een bestand"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/migratie/upload?entity=${entity}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${r.data.row_count} rijen ingelezen`);
      setActive(r.data);
      setMapping({});
      setDryRun(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setUploading(false);
  };

  const saveMapping = async () => {
    try {
      await api.post(`/migratie/${active.batch_id}/mapping`, { mapping });
      toast.success("Mapping opgeslagen");
      setActive({ ...active, status: "mapped" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runDryRun = async () => {
    try {
      const r = await api.post(`/migratie/${active.batch_id}/dry-run`);
      setDryRun(r.data);
      toast.success(`Dry-run: ${r.data.ok_count} ok · ${r.data.warning_count} waarschuwing · ${r.data.error_count} fout`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const commit = async () => {
    if (!window.confirm(`Definitief importeren? ${dryRun?.ok_count} rijen zonder fouten worden overgezet.`)) return;
    setCommitting(true);
    try {
      const r = await api.post(`/migratie/${active.batch_id}/commit`, { skip_warnings: false });
      toast.success(`${r.data.inserted} rijen geïmporteerd${r.data.skipped ? `, ${r.data.skipped} overgeslagen` : ""}`);
      setActive(null); setDryRun(null); setMapping({});
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setCommitting(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Migratiemachine"
        subtitle="Importeer ouders, kinderen of locaties uit CSV — 3 stappen: upload → mapping + dry-run → commit."
      />

      {/* Stepper */}
      <div className="flex items-center gap-2" data-testid="mig-steps">
        {[1, 2, 3].map(n => (
          <div key={n} className="flex items-center gap-2 flex-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                currentStep >= n ? "text-white" : "bg-neutral-100 text-neutral-500 border border-neutral-200"
              }`}
              style={currentStep >= n ? { background: "var(--sage)" } : {}}
              data-testid={`mig-step-${n}`}
            >{currentStep > n ? <CheckCircle2 className="h-4 w-4" /> : n}</div>
            <div className={`text-xs font-medium ${currentStep >= n ? "text-neutral-900" : "text-neutral-400"}`}>
              {n === 1 ? "Upload" : n === 2 ? "Mapping & dry-run" : "Commit"}
            </div>
            {n < 3 && <div className={`h-px flex-1`} style={{ background: currentStep > n ? "var(--sage)" : "#e5e5e5" }} />}
          </div>
        ))}
      </div>

      {!active && (
        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-medium">Stap 1 — Nieuwe import</h2>
          <div className="grid grid-cols-3 gap-3 max-w-2xl">
            <div>
              <Label>Entiteit</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger data-testid="mig-entity"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="parents">Ouders</SelectItem>
                  <SelectItem value="children">Kinderen</SelectItem>
                  <SelectItem value="locations">Locaties</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>CSV-bestand (max 10 MB)</Label>
              <div
                onDragEnter={e => { e.preventDefault(); setDragging(true); }}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDropFiles}
                className={`mt-1 border-2 border-dashed rounded-md p-4 text-center transition-all cursor-pointer ${
                  dragging ? "border-emerald-500 bg-emerald-50" : "border-neutral-200 bg-neutral-50 hover:border-emerald-300 hover:bg-emerald-50/40"
                }`}
                onClick={() => document.getElementById("mig-file")?.click()}
                data-testid="mig-dropzone"
              >
                <Upload className="h-6 w-6 mx-auto mb-1" style={{ color: "var(--sage)" }} />
                <div className="text-sm text-neutral-700">
                  {file ? <span className="font-medium">{file.name}</span> : <>Sleep je CSV hier of <span className="underline" style={{ color: "var(--sage)" }}>klik om te bladeren</span></>}
                </div>
                <div className="text-[11px] text-neutral-500 mt-1">Ondersteunde bestanden: .csv, .txt</div>
                <Input id="mig-file" type="file" accept=".csv,.txt" className="hidden"
                       onChange={e => setFile(e.target.files?.[0])}
                       data-testid="mig-file" />
              </div>
            </div>
          </div>
          <Button onClick={upload} disabled={uploading || !file}
                  data-testid="mig-upload-btn"
                  style={{ background: "var(--sage)" }} className="text-white">
            <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploaden…" : "Upload & analyseer"}
          </Button>
        </Card>
      )}

      {active && (
        <Card className="p-6 space-y-4" data-testid="mig-active-batch">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium">Stap 2 — Mapping + dry-run</h2>
              <div className="text-sm text-neutral-500">
                {active.entity} · {active.row_count} rijen · {active.headers?.length} kolommen
              </div>
            </div>
            <Button variant="ghost" onClick={() => { setActive(null); setDryRun(null); setMapping({}); }}>
              Annuleren
            </Button>
          </div>

          {/* Mapping */}
          <div className="border-t pt-4">
            <h3 className="font-medium mb-2">2a. Koppel bron-kolommen aan Webster-velden</h3>
            <div className="grid grid-cols-2 gap-2">
              {active.headers.map(col => (
                <div key={col} className="flex items-center gap-2">
                  <span className="text-xs bg-neutral-100 px-2 py-1 rounded flex-1 truncate" title={col}>{col}</span>
                  <span className="text-neutral-400">→</span>
                  <select value={mapping[col] || ""}
                          onChange={e => setMapping({ ...mapping, [col]: e.target.value })}
                          data-testid={`mig-map-${col}`}
                          className="text-sm border rounded px-2 py-1 bg-white flex-1">
                    <option value="">— skip —</option>
                    {ENTITY_FIELDS[active.entity].map(f =>
                      <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <Button size="sm" onClick={saveMapping} className="mt-3"
                    data-testid="mig-save-mapping"
                    style={{ background: "var(--sage)" }}>
              Mapping opslaan
            </Button>
          </div>

          {/* Dry-run */}
          {active.status !== "uploaded" && (
            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">2b. Dry-run — controleer resultaten voordat je committeert</h3>
              <Button size="sm" onClick={runDryRun} data-testid="mig-dryrun-btn" variant="outline">
                <PlayCircle className="h-4 w-4 mr-1" /> Valideer alle rijen
              </Button>
              {dryRun && (
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div className="border-2 border-emerald-300 bg-emerald-50 rounded p-3 text-center">
                    <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-600" />
                    <div className="text-2xl font-semibold text-emerald-800" data-testid="mig-ok-count">{dryRun.ok_count}</div>
                    <div className="text-xs text-emerald-700">Zonder fouten</div>
                  </div>
                  <div className="border-2 border-amber-300 bg-amber-50 rounded p-3 text-center">
                    <AlertTriangle className="h-5 w-5 mx-auto text-amber-600" />
                    <div className="text-2xl font-semibold text-amber-800">{dryRun.warning_count}</div>
                    <div className="text-xs text-amber-700">Waarschuwingen</div>
                  </div>
                  <div className="border-2 border-red-300 bg-red-50 rounded p-3 text-center">
                    <XCircle className="h-5 w-5 mx-auto text-red-600" />
                    <div className="text-2xl font-semibold text-red-800">{dryRun.error_count}</div>
                    <div className="text-xs text-red-700">Blokkerende fouten</div>
                  </div>
                </div>
              )}
              {dryRun && (dryRun.new_count > 0 || dryRun.duplicate_count > 0) && (
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="mig-diff-cards">
                  <div className="border-2 border-sky-300 bg-sky-50 rounded p-3">
                    <div className="text-xs uppercase tracking-widest text-sky-700 mb-1">Nieuw</div>
                    <div className="text-3xl font-semibold text-sky-900 tabular-nums" data-testid="mig-new-count">
                      {dryRun.new_count}
                    </div>
                    <div className="text-xs text-sky-700">Rijen die aangemaakt worden</div>
                  </div>
                  <div className="border-2 border-purple-300 bg-purple-50 rounded p-3">
                    <div className="text-xs uppercase tracking-widest text-purple-700 mb-1">Duplicaat (skip)</div>
                    <div className="text-3xl font-semibold text-purple-900 tabular-nums" data-testid="mig-dup-count">
                      {dryRun.duplicate_count}
                    </div>
                    <div className="text-xs text-purple-700">Bestaan al in Webster</div>
                  </div>
                </div>
              )}
              {dryRun?.errors?.length > 0 && (
                <div className="mt-3 text-xs bg-red-50 border border-red-200 rounded p-3 max-h-48 overflow-y-auto">
                  <div className="font-medium text-red-800 mb-1">Voorbeelden van fouten (max 20):</div>
                  {dryRun.errors.slice(0, 20).map((e, i) => (
                    <div key={i} className="text-red-700 py-0.5">
                      Rij {e.row_index + 1}: {e.issues.map(x => x.message).join(", ")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Commit */}
          {dryRun && (
            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">Stap 3 — Definitief importeren</h3>
              <p className="text-sm text-neutral-600 mb-2">
                Rijen met fouten worden overgeslagen. Waarschuwingen (duplicaten) worden ook geskipt.
              </p>
              <Button onClick={commit} disabled={committing || dryRun.ok_count === 0}
                      data-testid="mig-commit-btn"
                      style={{ background: "var(--sage)" }} className="text-white">
                {committing ? "Importeren…" : `Importeer ${dryRun.ok_count} rijen`}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Overzicht */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="font-medium">Eerdere imports</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
            <tr>
              <th className="text-left px-4 py-2">Bestand</th>
              <th className="text-left px-4 py-2">Entiteit</th>
              <th className="text-left px-4 py-2">Rijen</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Datum</th>
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 && (
              <tr><td colSpan={5} className="text-center text-neutral-500 py-8">Geen imports</td></tr>
            )}
            {batches.map(b => (
              <tr key={b.id} className="border-t border-neutral-100">
                <td className="px-4 py-2 truncate max-w-xs">{b.filename}</td>
                <td className="px-4 py-2">{b.entity}</td>
                <td className="px-4 py-2 tabular-nums">{b.row_count}</td>
                <td className="px-4 py-2">
                  <Badge className={
                    b.status === "committed" ? "bg-emerald-100 text-emerald-800 border-none" :
                    b.status === "dry_run" ? "bg-sky-100 text-sky-800 border-none" :
                    b.status === "mapped" ? "bg-amber-100 text-amber-800 border-none" :
                    "bg-neutral-100 text-neutral-600 border-none"
                  }>{b.status}</Badge>
                </td>
                <td className="px-4 py-2 text-neutral-600 text-xs">
                  {(b.created_at || "").slice(0, 16).replace("T", " ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
